import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { API_URL } from '../config';
import { BrandingApi } from '../api/resources';

const DEFAULT_NAME = 'aptcrm';
const CACHE_KEY = 'aptcrm.branding';

const BrandingContext = createContext(null);

function cached() {
  try {
    return JSON.parse(localStorage.getItem(CACHE_KEY)) || null;
  } catch (e) {
    return null;
  }
}

/**
 * CRM name + logo, fetched without sign-in (the login page uses them). The last value is cached so
 * the menu does not flash the default name on reload.
 */
export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(() => cached() || { name: DEFAULT_NAME, logo_url: null });

  const set = useCallback((next) => {
    setBranding(next);
    try { localStorage.setItem(CACHE_KEY, JSON.stringify(next)); } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    BrandingApi.get().then(set).catch(() => { /* keep cached / default */ });
  }, [set]);

  useEffect(() => {
    document.title = `${branding.name} Admin`;
  }, [branding.name]);

  const value = useMemo(
    () => ({
      name: branding.name || DEFAULT_NAME,
      logoUrl: branding.logo_url ? `${API_URL}${branding.logo_url}` : null,
      setBranding: set,
    }),
    [branding, set]
  );
  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export const useBranding = () => useContext(BrandingContext);
